import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Search, Menu, X, UserCircle2, LayoutDashboard, Globe } from "lucide-react";
import { SITE } from "@/constants/site";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import NotificationsBell from "./NotificationsBell";
import SearchOverlay from "./SearchOverlay";

const LINKS = [
  { id: "heritage", key: "nav_heritage" },
  { id: "schedule", key: "nav_schedule" },
  { id: "updates", key: "nav_updates" },
  { id: "gallery", key: "nav_gallery" },
  { id: "committee", key: "nav_committee" },
  { id: "donate", key: "nav_donate" },
  { id: "contact", key: "nav_contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, toggle, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(`#${id}`, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const langBtn = (
    <button
      onClick={toggle}
      data-testid="lang-toggle"
      className="h-10 px-3 rounded-full border border-[#F2B134]/30 inline-flex items-center gap-1.5 text-xs font-bold text-[#F2B134] hover:bg-[#F2B134]/10 transition-colors"
      aria-label="Change language"
    >
      <Globe size={14} /> {lang === "bn" ? "EN" : "বাংলা"}
    </button>
  );

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav border-b border-[#F2B134]/15 py-2" : "py-4"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
          <a href="#" onClick={goTo("home")} data-testid="nav-link-home" className="flex items-center gap-3 group">
            <span className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl overflow-hidden bg-[#FDF6EA] border border-[#F2B134]/40 shadow-[0_0_24px_-6px_rgba(242,177,52,0.6)] group-hover:scale-105 transition-transform">
              <img src="/logo.jpg" alt="কমিটির লোগো" className="w-full h-full object-cover" />
            </span>
            <span className="hidden md:block leading-tight">
              <span className="block font-bengali font-bold text-[#FDF6EA] text-sm">{SITE.shortBN}</span>
              <span className="block text-[10px] uppercase tracking-[0.22em] text-[#F2B134]/80">{SITE.subBN}</span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-6">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={goTo(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="text-sm text-[#D1BBA7] hover:text-[#F2B134] transition-colors"
              >
                {t(l.key)}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {langBtn}
            {user ? (
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="nav-dashboard-btn"
                className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#F2B134]/35 px-4 py-2 text-xs font-bold text-[#F2B134] hover:bg-[#F2B134]/10 transition-colors"
              >
                <LayoutDashboard size={13} /> {t("nav_dashboard")}
              </button>
            ) : (
              <button
                onClick={() => navigate("/login")}
                data-testid="nav-login-btn"
                className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#F2B134]/25 px-4 py-2 text-xs font-bold text-[#D1BBA7] hover:text-[#F2B134] hover:border-[#F2B134]/60 transition-colors"
              >
                <UserCircle2 size={13} /> {t("nav_login")}
              </button>
            )}
            <button
              data-testid="nav-search-btn"
              onClick={() => setSearchOpen(true)}
              className="h-10 w-10 rounded-full border border-[#F2B134]/25 grid place-items-center text-[#D1BBA7] hover:text-[#F2B134] hover:border-[#F2B134]/60 transition-colors"
              aria-label={t("nav_search_label")}
            >
              <Search size={17} />
            </button>
            <NotificationsBell />
            <a
              href="#donate"
              onClick={goTo("donate")}
              data-testid="nav-donate-btn"
              className="hidden sm:inline-flex btn-gold rounded-full px-5 py-2.5 text-sm font-bold"
            >
              {t("nav_donate_btn")}
            </a>
            <button
              data-testid="nav-mobile-toggle"
              className="lg:hidden h-10 w-10 rounded-full border border-[#F2B134]/25 grid place-items-center text-[#FDF6EA]"
              onClick={() => setOpen((v) => !v)}
              aria-label="মেনু"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:hidden glass-nav border-t border-[#F2B134]/15 mt-2 px-6 py-4 flex flex-col gap-4"
          >
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={goTo(l.id)}
                data-testid={`nav-link-mobile-${l.id}`}
                className="text-[#FDF6EA] text-base py-1"
              >
                {t(l.key)}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                navigate(user ? "/dashboard" : "/login");
              }}
              data-testid="nav-link-mobile-login"
              className="text-left text-[#F2B134] text-base py-1 font-bold"
            >
              {user ? t("nav_dashboard") : t("nav_login")}
            </button>
          </motion.nav>
        )}
      </motion.header>
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
